'use client';

import { ArrowRight, Loader2, Lock, Mail } from 'lucide-react';
import Link from 'next/link';
import { useActionState } from 'react';
import { useI18n } from '@/components/i18n-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TRANSLATIONS } from '@/i18n/config';
import { login, type TLoginState } from '@/lib/session-actions';

const initialState: TLoginState = { error: null, email: '' };

export function LoginForm() {
  const [state, formAction, pending] = useActionState(login, initialState);
  const { t } = useI18n();

  return (
    <form action={formAction} className="flex flex-col gap-5" noValidate>
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">{t(TRANSLATIONS.auth.email)}</Label>
        <div className="relative">
          <Mail className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder={t(TRANSLATIONS.auth.email_placeholder)}
            defaultValue={state.email}
            aria-invalid={Boolean(state.error)}
            className="h-11 pl-9"
            required
            autoFocus
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="password">{t(TRANSLATIONS.auth.password)}</Label>
          <Link href="/forgot-password" className="text-sm text-blue-600 hover:underline">
            {t(TRANSLATIONS.forgot_password.link)}
          </Link>
        </div>
        <div className="relative">
          <Lock className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            name="password"
            autoComplete="current-password"
            aria-invalid={Boolean(state.error)}
            className="h-11 pl-9"
            required
          />
        </div>
      </div>

      {state.error && (
        <p
          className="rounded-lg border border-red-300 bg-red-200/60 px-3 py-2.5 text-sm text-red-700 animate-in fade-in slide-in-from-top-1"
          role="alert"
        >
          {t(state.error)}
        </p>
      )}

      <Button
        type="submit"
        disabled={pending}
        className="group h-11 bg-linear-to-r from-blue-400 to-blue-600 text-base shadow-lg shadow-blue-400/25 transition-all hover:shadow-blue-400/40 hover:brightness-110"
      >
        {pending ? (
          <>
            <Loader2 className="animate-spin" />
            {t(TRANSLATIONS.auth.signing_in)}
          </>
        ) : (
          <>
            {t(TRANSLATIONS.auth.sign_in)}
            <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </Button>
    </form>
  );
}
